import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

const LandingPage = () => {
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactType, setContactType] = useState('pilot');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    message: '',
    website: '', // Honeypot field
  });
  const [submitting, setSubmitting] = useState(false);

  const handleContactClick = (type) => {
    setContactType(type);
    setShowContactModal(true);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/contact`, {
        ...contactForm,
        inquiryType: contactType,
      });
      toast.success('Your message has been sent! We will get back to you soon.');
      setShowContactModal(false);
      setContactForm({ name: '', email: '', phone: '', organization: '', message: '', website: '' });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ background: '#ffffff' }}>
      {/* Hero Section */}
      <section style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative shapes */}
        <div style={{
          position: 'absolute',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.1)',
          top: '-150px',
          right: '-100px',
        }}></div>
        <div style={{
          position: 'absolute',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
          bottom: '-100px',
          left: '-50px',
        }}></div>

        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="row align-items-center">
            <div className="col-lg-6 mb-5 mb-lg-0">
              <h1 style={{ 
                fontSize: '3.5rem', 
                fontWeight: '800', 
                marginBottom: '1.5rem',
                lineHeight: '1.2'
              }}>
                Secure online voting made simple
              </h1>
              <p style={{ fontSize: '1.25rem', marginBottom: '2rem', opacity: 0.95, lineHeight: '1.8' }}>
                Run elections, polls, and campaigns for your church, school, organization, or business. 
                Trusted by hundreds of organizations across Uganda.
              </p>
              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ fontSize: '1rem', opacity: 0.9, marginBottom: '0.5rem' }}>Choose how to get started:</p>
              </div>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <Link 
                  to="/register" 
                  className="btn btn-light btn-lg"
                  style={{ 
                    fontWeight: '600',
                    padding: '1rem 2rem',
                    fontSize: '1.125rem',
                    borderRadius: '50px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
                  }}
                >
                  <i className="bi bi-person-plus me-2"></i>
                  Join as voter
                </Link>
                <Link 
                  to="/register-organization" 
                  className="btn btn-outline-light btn-lg"
                  style={{ 
                    fontWeight: '600',
                    padding: '1rem 2rem',
                    fontSize: '1.125rem',
                    borderRadius: '50px',
                    borderWidth: '2px'
                  }}
                >
                  <i className="bi bi-building me-2"></i>
                  Register organization
                </Link>
              </div>
              <div style={{ marginTop: '1rem' }}>
                <Link 
                  to="/login" 
                  style={{ 
                    color: 'white',
                    textDecoration: 'underline',
                    opacity: 0.9,
                    fontSize: '1rem'
                  }}
                >
                  Already have an account? Sign in
                </Link>
              </div>
              <div style={{ marginTop: '2rem', display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: '2rem', fontWeight: '700' }}>500+</div>
                  <div style={{ opacity: 0.9 }}>Elections run</div>
                </div>
                <div>
                  <div style={{ fontSize: '2rem', fontWeight: '700' }}>50K+</div>
                  <div style={{ opacity: 0.9 }}>Votes cast</div>
                </div>
                <div>
                  <div style={{ fontSize: '2rem', fontWeight: '700' }}>99.9%</div>
                  <div style={{ opacity: 0.9 }}>Uptime</div>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div style={{
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                padding: '2rem',
                borderRadius: '20px',
                border: '1px solid rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '400px'
              }}>
                <svg width="100%" height="auto" viewBox="0 0 400 150" xmlns="http://www.w3.org/2000/svg" style={{ maxWidth: '400px' }}>
                  {/* Vote checkmark icon */}
                  <circle cx="50" cy="75" r="40" fill="white" opacity="0.15"/>
                  <path d="M 30 75 L 45 90 L 70 60" stroke="white" strokeWidth="6" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  
                  {/* VoteWave text */}
                  <text x="110" y="90" fontFamily="Arial, sans-serif" fontSize="48" fontWeight="800" fill="white" letterSpacing="-1">
                    VoteWave
                  </text>
                  
                  {/* Wave underline */}
                  <path d="M 110 105 Q 150 95 190 105 T 270 105" stroke="white" strokeWidth="3" fill="none" opacity="0.8"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section style={{ padding: '5rem 0', background: '#f9fafb' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
              Perfect for your organization
            </h2>
            <p style={{ fontSize: '1.125rem', color: '#6b7280', maxWidth: '700px', margin: '0 auto' }}>
              Trusted by diverse organizations for secure, transparent voting
            </p>
          </div>

          <div className="row g-4">
            {[
              {
                icon: 'bi-building',
                title: 'Churches & religious organizations',
                description: 'Run weekly polls, monthly awards, leadership elections, and fundraising campaigns with ease.',
                color: '#667eea'
              },
              {
                icon: 'bi-mortarboard',
                title: 'Schools & universities',
                description: 'Student elections, class representatives, teacher awards, and academic competitions.',
                color: '#10b981'
              },
              {
                icon: 'bi-broadcast',
                title: 'Media & entertainment',
                description: 'Weekly music polls, audience voting, talent shows, and social media campaigns.',
                color: '#f59e0b'
              },
              {
                icon: 'bi-briefcase',
                title: 'Corporate & business',
                description: 'Employee recognition, team decisions, board elections, and internal surveys.',
                color: '#8b5cf6'
              },
              {
                icon: 'bi-people',
                title: 'Associations & clubs',
                description: 'Member elections, committee voting, event planning, and community decisions.',
                color: '#ef4444'
              },
              {
                icon: 'bi-trophy',
                title: 'Events & competitions',
                description: 'Awards ceremonies, talent contests, sports voting, and fan engagement.',
                color: '#06b6d4'
              }
            ].map((item, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <div style={{
                  background: 'white',
                  padding: '2rem',
                  borderRadius: '12px',
                  height: '100%',
                  border: '2px solid #f3f4f6',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = item.color;
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#f3f4f6';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                >
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '12px',
                    background: `${item.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1.5rem'
                  }}>
                    <i className={item.icon} style={{ fontSize: '1.75rem', color: item.color }}></i>
                  </div>
                  <h5 style={{ fontWeight: '700', marginBottom: '1rem', color: '#1f2937' }}>
                    {item.title}
                  </h5>
                  <p style={{ color: '#6b7280', lineHeight: '1.7', marginBottom: 0 }}>
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section style={{ padding: '5rem 0' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
              Everything you need
            </h2>
            <p style={{ fontSize: '1.125rem', color: '#6b7280' }}>
              Powerful features to run professional elections
            </p>
          </div>

          <div className="row g-4">
            {[
              { icon: 'bi-shield-check', title: 'Secure & confidential', description: 'Bank-level encryption ensures vote confidentiality' },
              { icon: 'bi-phone', title: 'Mobile friendly', description: 'Vote from any device - phone, tablet, or computer' },
              { icon: 'bi-lightning', title: 'Real-time results', description: 'Live vote counting and instant result updates' },
              { icon: 'bi-cash-coin', title: 'Multi-voting option', description: 'Enable paid additional votes for fundraising' },
              { icon: 'bi-envelope', title: 'Email notifications', description: 'Automatic voter invitations and reminders' },
              { icon: 'bi-graph-up', title: 'Analytics & reports', description: 'Detailed insights and exportable reports' }
            ].map((feature, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <div>
                    <i className={feature.icon} style={{ 
                      fontSize: '1.5rem', 
                      color: '#667eea',
                      marginTop: '0.25rem'
                    }}></i>
                  </div>
                  <div>
                    <h6 style={{ fontWeight: '700', marginBottom: '0.5rem' }}>{feature.title}</h6>
                    <p style={{ color: '#6b7280', marginBottom: 0, fontSize: '0.938rem' }}>
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section style={{ padding: '5rem 0', background: '#f9fafb' }}>
        <div className="container text-center">
          <h2 style={{ fontSize: '2.5rem', fontWeight: '700' }}>Pricing tailored to your election</h2>
          <p className="text-muted my-4" style={{ fontSize: '1.125rem' }}>
            Every election is different. Tell us about your organisation, expected voters, and
            support needs, and we’ll prepare a quote that fits.
          </p>
          <p>Secure voting · Real-time results · Mobile-friendly access · Flexible support</p>
          <button
            className="btn btn-primary w-auto d-inline-flex align-items-center justify-content-center px-4 mt-3"
            style={{ flex: '0 0 auto', minHeight: '44px', borderRadius: '8px', fontSize: '0.9375rem' }}
            onClick={() => handleContactClick('quote')}
          >
            Request Quote
          </button>
        </div>
      </section>

      {/* Managed Voting Service Section */}
      <section style={{ padding: '5rem 0', background: 'white' }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '20px',
                padding: '3rem',
                color: 'white',
                height: '100%'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '1.5rem'
                }}>
                  <i className="bi bi-headset" style={{ fontSize: '2.5rem' }}></i>
                </div>
                <h3 style={{ fontWeight: '700', marginBottom: '1rem', fontSize: '1.75rem' }}>
                  Managed voting service
                </h3>
                <p style={{ fontSize: '1.125rem', opacity: 0.95, lineHeight: '1.8' }}>
                  Let our team handle your entire election process from start to finish. 
                  Perfect for large-scale events, high-stakes elections, or organizations 
                  that want expert support every step of the way.
                </p>
              </div>
            </div>
            <div className="col-lg-6">
              <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '1.5rem', color: '#1f2937' }}>
                Let us handle everything
              </h2>
              <div style={{ marginBottom: '2rem' }}>
                {[
                  { icon: 'bi-gear', title: 'Election setup & configuration', description: 'We configure your election with all positions, candidates, and voting rules' },
                  { icon: 'bi-people', title: 'Voter registration management', description: 'We handle voter lists, invitations, and access control' },
                  { icon: 'bi-eye', title: 'Real-time monitoring & support', description: 'Our team monitors your election and provides live support' },
                  { icon: 'bi-file-earmark-bar-graph', title: 'Results compilation & reporting', description: 'Receive comprehensive reports and certified results' }
                ].map((item, index) => (
                  <div key={index} style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem' }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '12px',
                      background: '#f3f4f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <i className={item.icon} style={{ fontSize: '1.25rem', color: '#667eea' }}></i>
                    </div>
                    <div>
                      <h6 style={{ fontWeight: '600', marginBottom: '0.25rem', color: '#1f2937' }}>{item.title}</h6>
                      <p style={{ color: '#6b7280', marginBottom: 0, fontSize: '0.875rem' }}>{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={() => handleContactClick('managed')}
                className="btn btn-lg"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  fontWeight: '600',
                  padding: '1rem 2rem',
                  borderRadius: '50px'
                }}
              >
                Get in touch
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{ 
        padding: '5rem 0',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <div className="container text-center">
          <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
            Ready to run your first election?
          </h2>
          <p style={{ fontSize: '1.25rem', marginBottom: '2rem', opacity: 0.95 }}>
            Join hundreds of organizations using VoteWave for secure, transparent voting
          </p>
          <Link 
            to="/register-organization"
            className="btn btn-light btn-lg"
            style={{
              fontWeight: '600',
              padding: '1rem 3rem',
              fontSize: '1.125rem',
              borderRadius: '50px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
            }}
          >
            Register your organization
          </Link>
          <div style={{ marginTop: '2rem', opacity: 0.9 }}>
            No credit card required • Set up in 5 minutes
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: '#1f2937', color: 'white', padding: '3rem 0' }}>
        <div className="container">
          <div className="row">
            <div className="col-lg-4 mb-4 mb-lg-0">
              <h4 style={{ fontWeight: '700', marginBottom: '1rem' }}>VoteWave</h4>
              <p style={{ opacity: 0.8, lineHeight: '1.8' }}>
                Secure online voting platform for organizations worldwide.
                Making democracy accessible and transparent.
              </p>
            </div>
            <div className="col-lg-4 mb-4 mb-lg-0">
              <h6 style={{ fontWeight: '600', marginBottom: '1rem' }}>Quick links</h6>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <Link to="/login" style={{ color: 'white', opacity: 0.8, textDecoration: 'none' }}>Sign in</Link>
                <Link to="/register" style={{ color: 'white', opacity: 0.8, textDecoration: 'none' }}>Create account</Link>
                <Link to="/register-organization" style={{ color: 'white', opacity: 0.8, textDecoration: 'none' }}>Register organization</Link>
                <Link to="/terms-and-conditions" style={{ color: 'white', opacity: 0.8, textDecoration: 'none' }}>Terms & conditions</Link>
                <Link to="/privacy-policy" style={{ color: 'white', opacity: 0.8, textDecoration: 'none' }}>Privacy policy</Link>
              </div>
            </div>
            <div className="col-lg-4">
              <h6 style={{ fontWeight: '600', marginBottom: '1rem' }}>Contact</h6>
              <div style={{ opacity: 0.8, lineHeight: '2' }}>
                <div>Email: info@votewave.co.ug</div>
                <div>Web: www.votewave.co.ug</div>
                <div style={{ marginTop: '1rem' }}>
                  <p style={{ marginBottom: '0.5rem' }}>Powered by Dcode Dynamics</p>
                </div>
              </div>
            </div>
          </div>
          <hr style={{ borderColor: 'rgba(255,255,255,0.1)', margin: '2rem 0' }} />
          <div className="text-center" style={{ opacity: 0.7 }}>
            © 2025 VoteWave. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="modal show d-block" role="dialog" aria-modal="true" aria-labelledby="contact-title" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header" style={{ borderBottom: '1px solid #e5e7eb' }}>
                <h5 id="contact-title" className="modal-title" style={{ fontWeight: '600' }}>
                  {contactType === 'quote' ? 'Request Quote' : contactType === 'pilot' ? 'Try VoteWave for free' : 'Managed voting service inquiry'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close contact form"
                  onClick={() => setShowContactModal(false)}
                ></button>
              </div>
              <form onSubmit={handleContactSubmit}>
                <div className="modal-body">
                  <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
                    {contactType === 'quote'
                      ? 'Tell us about your election and we’ll prepare a personalised quote.'
                      : contactType === 'pilot'
                      ? 'Fill in your details and our team will set you up with a free pilot to experience all VoteWave features.'
                      : 'Tell us about your event and our team will get in touch to discuss how we can help.'}
                  </p>
                  {/* Honeypot field - hidden from users */}
                  <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                    <label>Website</label>
                    <input
                      type="text"
                      name="website"
                      value={contactForm.website}
                      onChange={(e) => setContactForm({ ...contactForm, website: e.target.value })}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="contact-name">Full name *</label>
                    <input
                      id="contact-name"
                      maxLength={255}
                      type="text"
                      className="form-control"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="contact-email">Email address *</label>
                    <input
                      id="contact-email"
                      maxLength={254}
                      type="email"
                      className="form-control"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="contact-phone">Phone number</label>
                    <input
                      id="contact-phone"
                      maxLength={40}
                      type="tel"
                      className="form-control"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="contact-organisation">Organization name</label>
                    <input
                      id="contact-organisation"
                      maxLength={255}
                      type="text"
                      className="form-control"
                      value={contactForm.organization}
                      onChange={(e) => setContactForm({ ...contactForm, organization: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="contact-message">
                      {contactType === 'pilot' ? 'Tell us about your event (optional)' : 'Event details'}
                    </label>
                    <textarea
                      id="contact-message"
                      maxLength={5000}
                      className="form-control"
                      rows="3"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      placeholder={contactType !== 'pilot' ? 'Event type, expected voters, preferred dates...' : ''}
                    ></textarea>
                  </div>
                </div>
                <div className="modal-footer" style={{ borderTop: '1px solid #e5e7eb' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowContactModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn"
                    disabled={submitting}
                    style={{
                      background: contactType === 'pilot' ? '#f59e0b' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                    }}
                  >
                    {submitting ? 'Sending...' : contactType === 'quote' ? 'Request Quote' : 'Send message'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
